/**
 * The customer of a case in one line: in the mail card on the page, first in the work
 * column of the Outlook pane.
 *
 * Three states from `stripCustomerOf`, each with its icon (`CUSTOMER_ICON`): the name
 * with "Confirmed" when I2Q is certain of the account, or "Confirmed by sales" when a
 * person picked or confirmed it (two words since the sales review of 12 Oct 2026, C-06:
 * `CUSTOMER_CONFIRMED_WORDS`); "Confirm <Name, City>" with "Not them" while I2Q suggests
 * an account; "Find customer" when nobody was matched (for an agent's end customer
 * without an account the search starts with that name, `customerSearchStart` of the
 * customer card). "Why" opens a popover with the evidence, up to
 * three other candidates and "Find customer". The customer menu (round 4) sits last
 * while the case is open: "Change" with "Change customer" on a certain customer (plus
 * "Undo" when a person confirmed it, round 5), the three dots with "Change customer" and
 * "Not them" on a suggested one, "Find customer" when nobody was matched. A pick replaces
 * a certain customer; the prices are read again for the new account.
 *
 * Everything else about the customer (the role of the sender, the agent and the end
 * customer, earlier documents, the installed base, feedback) is in the "Customer" section
 * of the Details sheet. A confirmation is stored in the app database only.
 */

import { ChevronDown, Ellipsis, Icon, Search } from "@norican/icons";
import { Badge, Button, Menu, Popover, Typography } from "@norican/react";
import { useState } from "react";

import { type StripCustomer, stripCustomerOf } from "@/features/case-review/case-summary-strip";
import { placeOf } from "@/features/case-review/customer-card";
import {
  canActOnProposals,
  customerProposalOf,
  proposalIsFirstChoice,
} from "@/features/case-review/investigation";
import {
  ProposalActions,
  ProposalTag,
  ProposalWhy,
} from "@/features/case-review/investigation-panel";
import { type CaseWrites } from "@/features/case-review/use-case-writes";
import { type CustomerCandidate, personName } from "@/lib/api";
import { CUSTOMER_ICON } from "@/lib/line-icons";
import { logger } from "@/lib/logger";
import { type InvestigationProposal, type QuoteCaseDetail } from "@/lib/mock";
import {
  ACTION_LABELS,
  confirmsInOneClick,
  CUSTOMER_WHY_WORDS,
  customerConfirmedText,
  customerConfirmedWord,
  suggestedCustomerText,
} from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import {
  candidateKindLabel,
  candidateUseLabel,
  customerDecidedBySales,
  evidenceKindLabel,
  INVESTIGATION_WORDS,
} from "@/lib/work-labels";

/** Other candidates shown in "Why" before "Find customer". */
const WHY_CANDIDATES = 3;

export interface CustomerLineProps {
  detail: QuoteCaseDetail;
  writes: CaseWrites;
  /** Opens the customer search (PickCustomerDialog). */
  onFind: () => void;
}

/** Candidate kinds that come from the sender's own earlier documents (the API's kinds). */
const SENDER_PAST_KINDS: ReadonlySet<string> = new Set([
  "history",
  "contact_memory",
  "domain_memory",
]);

/**
 * The heading over the other accounts in "Why": on the mail of an agent, accounts that
 * all come from the agent's past are "Earlier end customers of this sender" (C-02); any
 * other list is "Other possible customers".
 */
export function whyHeading(
  detail: Pick<QuoteCaseDetail, "customerBlock">,
  others: readonly CustomerCandidate[],
): string {
  const agent = detail.customerBlock?.role === "agent";
  const past = others.length > 0 && others.every((c) => SENDER_PAST_KINDS.has(c.kind ?? ""));
  return agent && past ? CUSTOMER_WHY_WORDS.earlierOfSender : CUSTOMER_WHY_WORDS.others;
}

/** The candidates not shown on the line itself, for the "Why" popover. */
export function whyCandidates(
  detail: QuoteCaseDetail,
  customer: StripCustomer,
): CustomerCandidate[] {
  const shown = new Set<string>();
  if (customer.state === "suggested") {
    customer.options.forEach((option) => shown.add(option.account));
  }
  const confirmed = detail.work?.customer_account ?? detail.customerBlock?.account ?? null;
  if (customer.state === "certain" && confirmed) {
    shown.add(confirmed);
  }
  return (detail.customerBlock?.candidates ?? [])
    .filter((candidate) => !shown.has(candidate.account))
    .slice(0, WHY_CANDIDATES);
}

function WhyPopover({
  detail,
  customer,
  writes,
  onFind,
}: CustomerLineProps & { customer: StripCustomer }) {
  const [open, setOpen] = useState(false);
  const block = detail.customerBlock ?? null;
  const evidence = block?.evidence ?? [];
  const others = whyCandidates(detail, customer);
  const basis = block?.basis?.trim() || null;
  const confirmedBy = detail.work?.customer_by ?? null;
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        logger.debug("[case] customer why", { case: detail.id, open: next });
        if (next) {
          track("case.why_open", { what: "customer" }, { caseId: detail.id });
        }
        setOpen(next);
      }}
    >
      <Popover.Trigger
        render={<Button size="sm" variant="ghost" tone="neutral" aria-label="Why this customer" />}
      >
        {ACTION_LABELS.why}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="start" sideOffset={6}>
          <Popover.Popup className="max-w-sm">
            <Popover.Header>
              <Popover.Title>
                <Typography as="span" variant="label" className="text-fg font-bold">
                  Why this customer
                </Typography>
              </Popover.Title>
              <Popover.CloseButton />
            </Popover.Header>
            <Popover.Body className="flex flex-col gap-3">
              {confirmedBy && (
                <Typography variant="body-sm" tone="muted">
                  Confirmed by {personName(confirmedBy)}.
                </Typography>
              )}
              {basis && (
                <Typography variant="body-sm" className="text-fg break-words">
                  {basis}
                </Typography>
              )}
              {evidence.length > 0 && (
                <ul className="flex flex-col gap-0.5">
                  {evidence.map((entry, index) => {
                    const kind = evidenceKindLabel(entry.kind);
                    return (
                      <li key={index} className="text-fg-muted text-xs break-words">
                        {kind && <span className="text-fg-subtle font-semibold">{kind}: </span>}
                        {entry.text}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!basis && evidence.length === 0 && !confirmedBy && (
                <Typography variant="body-sm" tone="muted">
                  I2Q has no evidence for an account on this mail.
                </Typography>
              )}
              {others.length > 0 && (
                <div className="flex flex-col gap-1">
                  <Typography variant="caption" tone="subtle">
                    {whyHeading(detail, others)}
                  </Typography>
                  <ul className="flex flex-col">
                    {others.map((candidate) => {
                      const place = placeOf(candidate);
                      return (
                        <li
                          key={candidate.account}
                          className="border-border-subtle flex items-center gap-2 border-b py-1.5 last:border-b-0"
                        >
                          <span className="flex min-w-0 flex-1 flex-col text-xs">
                            <span className="text-fg font-semibold break-words">
                              {candidate.name}
                              <span className="text-fg-muted font-mono"> {candidate.account}</span>
                            </span>
                            {place && <span className="text-fg-muted">{place}</span>}
                            {/* Hunt round 3, Q-16: twin master rows, a person picks one. */}
                            {candidate.kind === "twin" && (
                              <Badge
                                tone="warning"
                                variant="outline"
                                size="xs"
                                className="w-fit"
                                data-twin-account=""
                              >
                                {candidateKindLabel("twin")}
                              </Badge>
                            )}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            tone="neutral"
                            disabled={writes.busy}
                            aria-label={candidateUseLabel(candidate)}
                            onClick={() => {
                              setOpen(false);
                              writes.confirmCustomer(candidate.account);
                            }}
                          >
                            Use this
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                className="w-fit"
                startIcon={<Icon icon={Search} size="xs" />}
                onClick={() => {
                  setOpen(false);
                  onFind();
                }}
              >
                {ACTION_LABELS.findCustomer}
              </Button>
            </Popover.Body>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * The customer menu: the last element of the line while the case is open. A certain
 * customer shows the word "Change" (there is no other button to change it); a
 * suggested or missing one shows the three dots, since Confirm, Not them or Find
 * customer already sit on the line.
 */
function CustomerMenu({
  state,
  busy,
  caseId,
  onFind,
  onUndo,
}: {
  state: StripCustomer["state"];
  busy: boolean;
  caseId: string;
  onFind: () => void;
  /** Takes a person's confirmation back (round 5); absent when I2Q is certain on its own. */
  onUndo?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const certain = state === "certain";
  /** The usage log: an entry of the customer menu was used. `item` is the id in the code. */
  const used = (item: "find_customer" | "change_customer" | "not_them" | "undo") =>
    track("case.menu_item", { menu: "customer", item }, { caseId });
  return (
    <Menu.Root
      open={open}
      onOpenChange={(next) => {
        logger.debug("[case] customer menu", { case: caseId, state, open: next });
        if (next) {
          track("case.menu_open", { menu: "customer" }, { caseId });
        }
        setOpen(next);
      }}
    >
      <Menu.Trigger
        render={
          certain ? (
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              endIcon={<Icon icon={ChevronDown} size="xs" />}
              aria-label={ACTION_LABELS.customerActions}
              data-customer-menu={state}
            />
          ) : (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              iconOnly
              aria-label={ACTION_LABELS.customerActions}
              data-customer-menu={state}
            />
          )
        }
      >
        {certain ? ACTION_LABELS.change : <Icon icon={Ellipsis} size="xs" />}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner align="end" sideOffset={6}>
          <Menu.Popup>
            <Menu.Item
              disabled={busy}
              onClick={() => {
                used(state === "none" ? "find_customer" : "change_customer");
                onFind();
              }}
            >
              {state === "none" ? ACTION_LABELS.findCustomer : ACTION_LABELS.changeCustomer}
            </Menu.Item>
            {state === "suggested" && (
              <Menu.Item
                disabled={busy}
                onClick={() => {
                  used("not_them");
                  onFind();
                }}
              >
                {ACTION_LABELS.notThem}
              </Menu.Item>
            )}
            {certain && onUndo && (
              <Menu.Item
                disabled={busy}
                aria-label="Undo the customer confirmation"
                data-customer-menu-item="undo"
                onClick={() => {
                  used("undo");
                  onUndo();
                }}
              >
                {ACTION_LABELS.undo}
              </Menu.Item>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/**
 * The account next to a proposal of the deeper search: on an agent's mail the engine
 * always lists the second account of the pair (the sender's own, or the end customer),
 * with one fixed sentence. Null when there is none, or when the line already offers it.
 */
export function otherAccountOf(
  detail: Pick<QuoteCaseDetail, "customerBlock" | "investigation">,
  proposal: InvestigationProposal | null,
  firstAccount: string | null | undefined,
): OtherAccount | null {
  if (!proposal) {
    return null;
  }
  // The engine names the second account of the pair itself, with its fixed sentence.
  const second = detail.investigation?.otherAccount ?? null;
  if (second?.account && second.account !== proposal.account && second.account !== firstAccount) {
    return { account: second.account, name: second.name, basis: second.reason || null };
  }
  // An older draft carries it only as a candidate of the deeper search.
  const candidate = (detail.customerBlock?.candidates ?? []).find(
    (entry) =>
      entry.kind === "investigation" &&
      entry.account !== proposal.account &&
      entry.account !== firstAccount,
  );
  return candidate
    ? { account: candidate.account, name: candidate.name, basis: candidate.basis ?? null }
    : null;
}

/** The second account of an agent pair as the customer line prints it. */
export interface OtherAccount {
  account: string;
  name: string;
  /** One fixed sentence: why this account is shown beside the proposal. */
  basis: string | null;
}

/**
 * The proposal of the deeper search under the customer line (round 2): the tag, the fixed
 * reason, "Why" with the lookups, and the two answers. When the proposal is the account
 * the line offers first, the line's own "Confirm" confirms it and this row carries "Not
 * this one"; a proposal with a doubt, or one beside another first choice, is a candidate
 * row with its own outline "Confirm". The second account of an agent pair follows as
 * "Other account".
 */
function CustomerProposal({
  detail,
  proposal,
  firstChoice,
  other,
  writes,
}: {
  detail: QuoteCaseDetail;
  proposal: InvestigationProposal;
  firstChoice: boolean;
  other: OtherAccount | null;
  writes: CaseWrites;
}) {
  const account = proposal.account ?? "";
  const mayAct = canActOnProposals(detail);
  return (
    <div
      className="flex flex-col gap-1"
      data-customer-proposal={proposal.id}
      data-proposal-status={proposal.status}
      data-proposal-first={firstChoice ? "yes" : "no"}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <ProposalTag proposal={proposal} />
        {!firstChoice && (
          <Typography as="span" variant="body-sm" className="text-fg break-words">
            {proposal.name || "Account"}
            <span className="text-fg-muted font-mono"> {account}</span>
          </Typography>
        )}
        <ProposalWhy proposal={proposal} caseId={detail.id} />
        <ProposalActions
          detail={detail}
          proposal={proposal}
          writes={writes}
          showConfirm={!firstChoice}
          onConfirm={() => writes.confirmCustomer(account)}
        />
      </div>
      {proposal.doubt ? (
        <Typography variant="caption" className="text-warning-fg break-words">
          {proposal.doubt}
        </Typography>
      ) : (
        proposal.reason && (
          <Typography variant="caption" tone="muted" className="break-words">
            {proposal.reason}
          </Typography>
        )
      )}
      {other && (
        <div className="flex flex-wrap items-center gap-1.5" data-other-account={other.account}>
          <Typography as="span" variant="caption" tone="subtle">
            {INVESTIGATION_WORDS.otherAccount}
          </Typography>
          <Typography as="span" variant="body-sm" className="text-fg break-words">
            {other.name}
            <span className="text-fg-muted font-mono"> {other.account}</span>
          </Typography>
          {mayAct && (
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              disabled={writes.busy}
              aria-label={`Use ${other.name || other.account} as the customer`}
              onClick={() => writes.confirmCustomer(other.account)}
            >
              Use this
            </Button>
          )}
          {other.basis && (
            <Typography variant="caption" tone="muted" className="w-full break-words">
              {other.basis}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}

/** The one customer line: name and certainty, Confirm or Find, Why, and the customer menu. */
export function CustomerLine({ detail, writes, onFind }: CustomerLineProps) {
  const customer = stripCustomerOf(detail);
  const open = (detail.workStatus ?? "open") === "open";
  // The proposal of the deeper search, when it is not the account the case is settled on.
  const proposal = customerProposalOf(detail);
  // An account a person rejected as a proposal is not offered as the first choice again.
  const rejected = proposal?.decision === "rejected" ? proposal.account : null;
  const options =
    customer.state === "suggested"
      ? customer.options.filter((option) => option.account !== rejected)
      : [];
  const first = options[0];
  const firstChoice = proposalIsFirstChoice(proposal, first?.account);
  const other = otherAccountOf(detail, proposal, first?.account);
  // Who decided a certain customer: a person ("Confirmed by sales") or I2Q ("Confirmed").
  const bySales = customerDecidedBySales(detail);
  // A suggestion with evidence is one click (solid); a guess takes a deliberate click.
  const oneClick = Boolean(first && confirmsInOneClick(first));
  // The "reads the prices again" sentence holds only while I2Q reads the case again
  // (state queued or processing after the confirmation); it stayed under the line after
  // every later write and after the confirmation was taken back (tester round 5).
  const readingAgain = detail.state === "queued" || detail.state === "processing";
  const confirmedName =
    writes.confirmedAccount && readingAgain && customer.state === "certain" ? customer.name : null;

  return (
    <div className="flex flex-col gap-1" data-customer-line={customer.state}>
      <div className="flex flex-wrap items-center gap-2">
        {customer.state === "certain" ? (
          <>
            <Typography as="span" variant="body-sm" className="text-fg font-semibold break-words">
              {customer.name || "Customer"}
            </Typography>
            <Badge tone="success" variant="subtle" size="sm">
              <span
                className="inline-flex items-center gap-1"
                data-customer-confirmed={bySales ? "sales" : "i2q"}
              >
                <Icon icon={CUSTOMER_ICON.certain} size="xs" />
                {customerConfirmedWord(bySales)}
              </span>
            </Badge>
          </>
        ) : customer.state === "suggested" && first ? (
          <>
            <Button
              size="sm"
              tone="accent"
              variant={oneClick ? "solid" : "outline"}
              loading={writes.busy}
              disabled={!open}
              startIcon={<Icon icon={CUSTOMER_ICON.suggested} size="xs" />}
              className="h-auto max-w-full text-left whitespace-normal"
              onClick={() => writes.confirmCustomer(first.account)}
            >
              {ACTION_LABELS.confirmCustomer(suggestedCustomerText(first, customer.options))}
            </Button>
            {/* The first choice is a proposal of the deeper search: its row below carries
                "Not this one", which also records the rejection. */}
            {!firstChoice && (
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                disabled={writes.busy || !open}
                onClick={onFind}
              >
                {ACTION_LABELS.notThem}
              </Button>
            )}
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={!open}
            startIcon={<Icon icon={CUSTOMER_ICON.none} size="xs" />}
            onClick={onFind}
          >
            {ACTION_LABELS.findCustomer}
          </Button>
        )}
        <WhyPopover detail={detail} customer={customer} writes={writes} onFind={onFind} />
        {open && (
          <CustomerMenu
            state={customer.state}
            busy={writes.busy}
            caseId={detail.id}
            onFind={onFind}
            // A confirmation a person made can be taken back here, like a line's "Undo".
            onUndo={
              detail.work?.customer_by && detail.work.customer_account
                ? () => writes.confirmCustomer(null)
                : undefined
            }
          />
        )}
      </div>
      {proposal && (
        <CustomerProposal
          detail={detail}
          proposal={proposal}
          firstChoice={firstChoice}
          other={other}
          writes={writes}
        />
      )}
      {confirmedName && (
        <Typography variant="caption" tone="muted" role="status">
          {customerConfirmedText(confirmedName)}
        </Typography>
      )}
      {writes.error && (
        <Typography variant="caption" className="text-danger-fg" role="alert">
          {writes.error}
        </Typography>
      )}
    </div>
  );
}
